import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, Trash2, LogOut, Mail, Building2, Phone, Clock, CheckCircle, Circle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loginError, setLoginError] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, []);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLoggedIn(true);
      fetchContacts();
    } catch {
      localStorage.removeItem('admin_token');
      setToken('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const t = res.data.token;
      localStorage.setItem('admin_token', t);
      setToken(t);
      setIsLoggedIn(true);
      fetchContacts(t);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Invalid credentials');
    }
  };

  const fetchContacts = async (t) => {
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${t || token}` }
      });
      setContacts(res.data);
    } catch { /* ignore */ }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/contact/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(prev => prev.map(c => c.id === id ? { ...c, is_read: true } : c));
      if (selectedContact?.id === id) setSelectedContact(prev => ({ ...prev, is_read: true }));
    } catch { /* ignore */ }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(prev => prev.filter(c => c.id !== id));
      if (selectedContact?.id === id) setSelectedContact(null);
    } catch { /* ignore */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center px-6">
        <div className="w-full max-w-sm" data-testid="admin-login-container">
          <div className="text-center mb-10">
            <h1
              className="text-2xl font-bold text-[#333F48] mb-2"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Admin Panel
            </h1>
            <p className="text-sm text-[#333F48]/60" style={{ fontFamily: 'Roboto, sans-serif' }}>
              AFRICAINOX Contact Management
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
                className="w-full bg-white border border-[#C8C9C7] focus:border-[#003865] outline-none px-4 py-3 text-[#333F48] transition-colors"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#333F48]/60 mb-2 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
                className="w-full bg-white border border-[#C8C9C7] focus:border-[#003865] outline-none px-4 py-3 text-[#333F48] transition-colors"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-sm text-red-600">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full bg-[#003865] text-white py-3 font-semibold uppercase tracking-wide text-sm hover:bg-[#004B87] transition-colors"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  const unreadCount = contacts.filter(c => !c.is_read).length;

  return (
    <div className="min-h-screen bg-[#F8F8F7]" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-[#C8C9C7] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-[#333F48]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Contact Submissions
            </h1>
            <p className="text-xs text-[#333F48]/50" style={{ fontFamily: 'Roboto, sans-serif' }}>
              {contacts.length} total &middot; {unreadCount} unread
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 text-sm text-[#333F48]/60 hover:text-[#333F48] transition-colors"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 space-y-2" data-testid="contact-list">
            {contacts.length === 0 ? (
              <p className="text-sm text-[#333F48]/50 text-center py-12" style={{ fontFamily: 'Roboto, sans-serif' }}>
                No contact submissions yet
              </p>
            ) : (
              contacts.map(c => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedContact(c); if (!c.is_read) markRead(c.id); }}
                  data-testid={`contact-item-${c.id}`}
                  className={`w-full text-left p-4 border transition-colors duration-200 ${
                    selectedContact?.id === c.id
                      ? 'bg-[#003865]/5 border-[#003865]'
                      : 'bg-white border-[#C8C9C7] hover:border-[#003865]/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {c.is_read ? (
                        <CheckCircle size={14} className="text-[#C8C9C7] flex-shrink-0" />
                      ) : (
                        <Circle size={14} className="text-[#003865] flex-shrink-0 fill-[#003865]" />
                      )}
                      <span
                        className={`text-sm truncate ${c.is_read ? 'text-[#333F48]/60' : 'font-semibold text-[#333F48]'}`}
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {c.name}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-[#333F48]/40 mt-1 truncate pl-6" style={{ fontFamily: 'Roboto, sans-serif' }}>
                    {c.message}
                  </p>
                </button>
              ))
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2" data-testid="contact-detail">
            {selectedContact ? (
              <div className="bg-white border border-[#C8C9C7] p-8">
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <h2
                      className="text-xl font-bold text-[#333F48]"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {selectedContact.name}
                    </h2>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock size={12} className="text-[#333F48]/40" />
                      <span className="text-xs text-[#333F48]/40" style={{ fontFamily: 'Roboto, sans-serif' }}>
                        {new Date(selectedContact.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteContact(selectedContact.id)}
                    data-testid="delete-contact-button"
                    className="p-2 text-[#333F48]/40 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 pb-8 border-b border-[#C8C9C7]">
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="text-[#003865]" />
                    <span className="text-sm text-[#333F48]" style={{ fontFamily: 'Roboto, sans-serif' }}>{selectedContact.email}</span>
                  </div>
                  {selectedContact.company && (
                    <div className="flex items-center gap-3">
                      <Building2 size={16} className="text-[#003865]" />
                      <span className="text-sm text-[#333F48]" style={{ fontFamily: 'Roboto, sans-serif' }}>{selectedContact.company}</span>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-[#003865]" />
                      <span className="text-sm text-[#333F48]" style={{ fontFamily: 'Roboto, sans-serif' }}>{selectedContact.phone}</span>
                    </div>
                  )}
                </div>

                <div>
                  <h3
                    className="text-xs font-bold uppercase tracking-widest text-[#333F48]/40 mb-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    Message
                  </h3>
                  <p className="text-sm leading-relaxed text-[#333F48]/80 whitespace-pre-wrap" style={{ fontFamily: 'Roboto, sans-serif' }}>
                    {selectedContact.message}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-white border border-[#C8C9C7]">
                <div className="text-center">
                  <Eye size={32} className="text-[#C8C9C7] mx-auto mb-3" />
                  <p className="text-sm text-[#333F48]/40" style={{ fontFamily: 'Roboto, sans-serif' }}>
                    Select a submission to view details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
